import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import CategoryCard from "../components/CategoryCard";
import ProductCard from "../components/ProductCard";
import CartSheet from "../components/CartSheet";
import Footer from "../components/Footer";
import FloatingTelegram from "../components/FloatingTelegram";
import InfoDialog from "../components/InfoDialog";
import SplashHome from "../components/SplashHome";
import IpfsImage from "../components/IpfsImage";
import { LegalDisclaimer } from "../components/LegalDisclaimer";
import { loadCatalog, TELEGRAM_URL, TELEGRAM_HANDLE } from "../lib/api";
import { preloadImages, fastIpfsUrl } from "../lib/media";

// Visual configuration for each category (background image + fallback gradient).
// These are used ONLY as fallback when category.image_url is not set in DB.
// Image CIDs come from env vars for portability; gradients are pure CSS.
const FALLBACK_GATEWAY = "https://nomelupin.mypinata.cloud/ipfs";
const CATEGORY_VISUAL = {
    "antipasti": {
        image: process.env.REACT_APP_FALLBACK_ANTIPASTI_CID
            ? `${FALLBACK_GATEWAY}/${process.env.REACT_APP_FALLBACK_ANTIPASTI_CID}`
            : null,
        gradient: "bg-gradient-to-b from-[#8B4513] via-[#C9914F] to-[#5C2A0E]",
    },
    "pasta-fresca": {
        image: process.env.REACT_APP_FALLBACK_PASTA_CID
            ? `${FALLBACK_GATEWAY}/${process.env.REACT_APP_FALLBACK_PASTA_CID}`
            : null,
        gradient: "bg-gradient-to-b from-[#F5DEB3] via-[#E8B85A] to-[#A0763D]",
    },
    "dolci": {
        image: process.env.REACT_APP_FALLBACK_DOLCI_CID
            ? `${FALLBACK_GATEWAY}/${process.env.REACT_APP_FALLBACK_DOLCI_CID}`
            : null,
        gradient: "bg-gradient-to-b from-[#D2691E] via-[#F4A47C] to-[#8B4513]",
    },
};

const CATALOG_CACHE_KEY = "catalog_cache_v1";
const CATALOG_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes — beyond this we prefer to skeleton-load fresh data rather than flash stale images.

const Home = () => {
    // Try to seed initial state from localStorage cache so returning users see
    // the last-known catalog instantly (no "empty catalog" flash on slow IPFS).
    // However we IGNORE stale cache older than the TTL because otherwise a
    // recently-changed category photo would flash the previous image before
    // being replaced by the fresh backend response.
    const cached = React.useMemo(() => {
        try {
            const raw = localStorage.getItem(CATALOG_CACHE_KEY);
            if (!raw) return null;
            const c = JSON.parse(raw);
            if (!c || typeof c !== "object") return null;
            const savedAt = Number(c._cached_at || 0);
            if (!savedAt || Date.now() - savedAt > CATALOG_CACHE_TTL_MS) {
                // Stale — treat as cache-miss so we render a skeleton instead
                return null;
            }
            return c;
        } catch (_) { return null; }
    }, []);
    const [categories, setCategories] = useState(cached?.categories || []);
    const [subcategories, setSubcategories] = useState(cached?.subcategories || []);
    const [products, setProducts] = useState(cached?.products || []);
    const [info, setInfo] = useState(cached?.info || { title: "Info", description: "", links: [] });
    const [badges, setBadges] = useState(cached?.badges || []);
    const [reviews, setReviews] = useState(cached?.reviews || []);
    // Splash glitter home — open by default on first load
    const [splashOpen, setSplashOpen] = useState(true);
    // Splash open by default — closed only after catalog load reveals empty info
    const [infoOpen, setInfoOpen] = useState(false);
    const [infoMode, setInfoMode] = useState("all"); // all | links | info
    const [activeCat, setActiveCat] = useState(null);
    const [activeSub, setActiveSub] = useState(null);
    const [search, setSearch] = useState("");
    // If we already have cached data, don't start in loading state (avoids empty flash)
    const [loading, setLoading] = useState(!cached);
    const productsRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        loadCatalog()
            .then((c) => {
                if (cancelled) return;
                const cats = c.categories || [];
                const subs = c.subcategories || [];
                const prods = c.products || [];
                setCategories(cats);
                setSubcategories(subs);
                setProducts(prods);
                setBadges(c.badges || []);
                setReviews(c.reviews || []);
                if (c.info) {
                    setInfo(c.info);
                    // Close splash if there is NO content (empty info AND no links)
                    const hasContent = (c.info.description || "").trim().length > 0 || (c.info.links || []).length > 0;
                    if (!hasContent) setSplashOpen(false);
                } else {
                    setSplashOpen(false);
                }
                // Cache fresh catalog for next visit (fast paint on return)
                try {
                    localStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify({
                        categories: cats,
                        subcategories: subs,
                        products: prods,
                        info: c.info || null,
                        badges: c.badges || [],
                        reviews: c.reviews || [],
                        _cached_at: Date.now(),
                    }));
                } catch (_) { /* quota exceeded — ignore */ }
                // Pre-warm the service-worker + browser cache with EVERY thumbnail
                // so returning visitors see the whole catalog instantly.
                // Category images load first (above-the-fold), then all product photos.
                const catUrls = cats
                    .map((cat) => cat.image_url || CATEGORY_VISUAL[cat.slug]?.image)
                    .filter(Boolean)
                    .map(fastIpfsUrl);
                const prodUrls = prods
                    .map((p) => p.photo_url)
                    .filter(Boolean)
                    .map(fastIpfsUrl);
                preloadImages(catUrls);
                // Defer product preload slightly so category images and UI render first.
                setTimeout(() => preloadImages(prodUrls), 400);
            })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, []);

    const openCategory = (catId) => {
        setActiveCat(catId);
        setActiveSub(null);
        setTimeout(() => productsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    };

    const filteredProducts = products.filter((p) => {
        if (activeCat && p.category_id !== activeCat) return false;
        if (activeSub && p.subcategory_id !== activeSub) return false;
        if (search && !p.name?.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    }).sort((a, b) => {
        const sa = typeof a.sort_order === "number" ? a.sort_order : 999999;
        const sb = typeof b.sort_order === "number" ? b.sort_order : 999999;
        if (sa !== sb) return sa - sb;
        // fallback: by created_at desc
        return (b.created_at || "").localeCompare(a.created_at || "");
    });

    const subsForActiveCat = activeCat
        ? subcategories.filter((s) => s.category_id === activeCat).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        : [];

    const activeCategoryName = activeCat
        ? categories.find((c) => c.id === activeCat)?.name
        : null;

    return (
        <div className="min-h-screen" data-testid="home-page">
            <Header onBack={() => setSplashOpen(true)} onOpenLinks={() => { setInfoMode("links"); setInfoOpen(true); }} />

            {/* Legal disclaimer — visible on every catalog view */}
            <LegalDisclaimer variant="banner" />

            {/* 100% reship marquee banner */}
            <div className="relative overflow-hidden bg-black/90 border-y-2 border-[#7c3aed] py-2.5 shadow-[0_0_24px_rgba(124,58,237,0.6)]" data-testid="reship-banner">
                <div className="flex animate-marquee whitespace-nowrap">
                    {Array.from({ length: 12 }).map((_, i) => (
                        <span key={i} className="font-display text-2xl sm:text-3xl tracking-wider text-[#e879f9] drop-shadow-[0_0_10px_rgba(232,121,249,0.85)] mx-8">
                            ✦ 100% RESHIP ✦
                        </span>
                    ))}
                </div>
            </div>

            {/* Category cards section — Lupin style */}
            <section id="menu-section" className="px-4 pb-12 pt-5 bg-black/35 backdrop-blur-sm border-t border-[#7c3aed]/15" data-testid="menu-section">
                <div className="max-w-5xl mx-auto">
                    {/* Small circular avatar (Lupin-style) */}
                    <div className="flex justify-center mb-3" data-testid="menu-avatar">
                        <div className="relative w-24 h-24 sm:w-28 sm:h-28">
                            <div className="absolute -inset-2 rounded-full bg-[#7c3aed]/30 blur-xl" />
                            <div className="absolute inset-0 rounded-full bg-[#0d0418] border-2 border-[#e879f9]/70 shadow-[0_0_20px_rgba(232,121,249,0.45)] overflow-hidden">
                                <IpfsImage
                                    src="https://nomelupin.mypinata.cloud/ipfs/bafkreifec5caqgaskmdnbkkcbuscnrodmqqbnq34rw22cxfw3fkl67wiru"
                                    alt="Il Vero Pinko"
                                    loading="eager"
                                    fetchPriority="high"
                                    decoding="async"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Title with sparkles */}
                    <h2 className="text-center font-serif-display text-3xl sm:text-4xl text-white mb-5 tracking-tight">
                        <span className="text-[#e879f9] drop-shadow-[0_0_10px_rgba(232,121,249,0.6)]">✦</span>
                        <span className="mx-2">Scopri i nostri prodotti</span>
                        <span className="text-[#e879f9] drop-shadow-[0_0_10px_rgba(232,121,249,0.6)]">✦</span>
                    </h2>

                    {/* Search bar */}
                    <div className="max-w-md mx-auto mb-5">
                        <input
                            type="text"
                            placeholder="Cerca un prodotto..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full px-5 py-3 rounded-full bg-[#150826]/85 border border-[#7c3aed]/45 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#e879f9] focus:shadow-[0_0_18px_rgba(232,121,249,0.35)] transition-all"
                            data-testid="search-input-top"
                        />
                    </div>

                    {/* Categories grid 2-col */}
                    {loading && categories.length === 0 ? (
                        <div className="text-center py-20 font-mono text-sm text-white/50">
                            Caricamento dal network IPFS…
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            {categories.map((cat, i) => {
                                const visual = CATEGORY_VISUAL[cat.slug] || {};
                                const image = cat.image_url || visual.image;
                                const prodCount = products.filter((p) => p.category_id === cat.id).length;
                                return (
                                    <CategoryCard
                                        key={cat.id}
                                        category={cat}
                                        onOpen={() => openCategory(cat.id)}
                                        image={image}
                                        count={prodCount}
                                        index={i}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* Products section (visible after clicking "Apri" on a category) */}
            <section ref={productsRef} className="px-4 pb-16 pt-4" data-testid="products-section">
                <div className="max-w-5xl mx-auto">
                    {activeCat && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-[#0d0418]/80 backdrop-blur rounded-2xl border border-[#7c3aed]/40 shadow-[0_0_18px_rgba(124,58,237,0.25)] p-5 sm:p-7 mb-6"
                        >
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#e879f9] mb-1">
                                        Categoria
                                    </div>
                                    <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold text-white leading-none">
                                        {activeCategoryName}
                                    </h2>
                                </div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="text"
                                        placeholder="Cerca..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="px-4 py-2 rounded-full border border-[#7c3aed]/45 bg-[#150826]/85 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#e879f9] focus:shadow-[0_0_14px_rgba(232,121,249,0.3)] w-40 sm:w-56"
                                        data-testid="search-input"
                                    />
                                    <button
                                        onClick={() => setActiveCat(null)}
                                        className="px-4 py-2 rounded-full bg-[#150826]/85 border border-[#7c3aed]/50 text-[#e879f9] text-sm font-medium hover:border-[#e879f9] hover:shadow-[0_0_12px_rgba(232,121,249,0.3)] transition-all"
                                        data-testid="back-to-categories"
                                    >
                                        ← Categorie
                                    </button>
                                </div>
                            </div>
                            {subsForActiveCat.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#7c3aed]/25" data-testid="subcategory-chips">
                                    <button
                                        onClick={() => setActiveSub(null)}
                                        data-testid="sub-chip-all"
                                        className={`px-4 py-1.5 rounded-full text-sm font-mono font-semibold border transition-all ${
                                            !activeSub
                                                ? "bg-[#7c3aed] text-white border-[#e879f9] shadow-[0_0_12px_rgba(232,121,249,0.45)]"
                                                : "bg-[#150826]/85 text-white/80 border-[#7c3aed]/45 hover:border-[#e879f9]"
                                        }`}
                                    >
                                        Tutti
                                    </button>
                                    {subsForActiveCat.map((s) => (
                                        <button
                                            key={s.id}
                                            onClick={() => setActiveSub(s.id)}
                                            data-testid={`sub-chip-${s.id}`}
                                            className={`px-4 py-1.5 rounded-full text-sm font-mono font-semibold border transition-all ${
                                                activeSub === s.id
                                                    ? "bg-[#7c3aed] text-white border-[#e879f9] shadow-[0_0_12px_rgba(232,121,249,0.45)]"
                                                    : "bg-[#150826]/85 text-white/80 border-[#7c3aed]/45 hover:border-[#e879f9]"
                                            }`}
                                        >
                                            {s.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {activeCat && (
                        loading && filteredProducts.length === 0 ? (
                            // Loading skeleton — prevents the "empty catalog" flash on first paint
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6" data-testid="products-loading">
                                {[0, 1, 2, 3].map((i) => (
                                    <div key={i} className="rounded-2xl border border-[#7c3aed]/20 bg-[#0a0610] overflow-hidden animate-pulse">
                                        <div className="aspect-[4/3] bg-gradient-to-br from-[#1a0b2e] via-[#0d0418] to-[#1a0b2e]" />
                                        <div className="p-4 space-y-2">
                                            <div className="h-4 bg-[#7c3aed]/20 rounded w-3/4" />
                                            <div className="h-3 bg-[#7c3aed]/15 rounded w-1/2" />
                                            <div className="h-8 bg-[#7c3aed]/15 rounded-full mt-3" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="bg-[#0d0418]/80 backdrop-blur rounded-2xl border border-dashed border-[#7c3aed]/45 text-center py-16 px-6" data-testid="empty-products">
                                <div className="font-serif-display text-3xl mb-2 text-white">
                                    Nessun prodotto qui.
                                </div>
                                <p className="text-white/55 max-w-md mx-auto">
                                    Apri Telegram e usa il bot per aggiungere prodotti a questa categoria.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6" data-testid="products-grid">
                                {filteredProducts.map((p, i) => (
                                    <ProductCard key={p.id} product={p} index={i} />
                                ))}
                            </div>
                        )
                    )}
                </div>
            </section>

            <Footer telegramUrl={TELEGRAM_URL} />
            <FloatingTelegram url={TELEGRAM_URL} />
            <CartSheet />
            <InfoDialog open={infoOpen} onClose={() => { setInfoOpen(false); setInfoMode("all"); }} info={info} mode={infoMode} />
            <SplashHome open={splashOpen} onClose={() => setSplashOpen(false)} info={info} badges={badges} reviews={reviews} telegramHandle={TELEGRAM_HANDLE || "il_vero_pinko"} />
        </div>
    );
};

export default Home;
